import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import FramedImage from '../components/FramedImage';
import WishlistIcon from '../components/WishlistIcon';
import { DEFAULT_PREPARATION_DAYS, formatBusinessDays } from '../lib/preparationTime';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { addToCart, cartLoading } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToast, setAddedToast] = useState(false);
  const [wishlistSaving, setWishlistSaving] = useState(false);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/products/slug/${slug}`);
        if (!res.ok) {
          throw new Error('Product not found.');
        }
        const data = await res.json();
        setProduct(data);
        if (data.variants && data.variants.length > 0) {
          setSelectedVariant(data.variants[0]);
        } else {
          setSelectedVariant(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load product details.');
      } finally {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading product...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning py-4">
          <h5>Product Not Found</h5>
          <p className="text-muted small">The requested product could not be located.</p>
          <Link to="/shop" className="btn btn-primary-custom rounded-pill">
            ← Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const {
    title,
    subtitle,
    description,
    short_description,
    price_mrp,
    final_price,
    product_discount_pct,
    category_discount_pct,
    site_discount_pct,
    savings,
    category_name,
    category_slug,
    stock,
    sku,
    weight,
    dimensions,
    preparation_days,
    origin_label,
    origin_city,
    gallery = [],
    variants = [],
  } = product;

  const images = gallery.length > 0 ? gallery.map((g) => g.url) : [
    'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=800&q=80',
  ];
  const displayedMrp = Number(selectedVariant?.price_mrp ?? price_mrp);
  const displayedPrice = Number(selectedVariant?.final_price ?? final_price);
  const displayedSavings = Number(selectedVariant?.savings ?? savings ?? Math.max(0, displayedMrp - displayedPrice));
  const displayedStock = Number(selectedVariant?.stock ?? stock ?? 0);
  const selectedVariantLabel = selectedVariant?.variant_name
    || [selectedVariant?.size, selectedVariant?.color, selectedVariant?.material].filter(Boolean).join(' · ')
    || selectedVariant?.sku
    || '';

  const handleAddToCart = () => {
    addToCart({
      ...product,
      variant_id: selectedVariant?.id || null,
      variant_name: selectedVariantLabel,
      price_mrp: displayedMrp,
      final_price: displayedPrice,
      images,
    }, quantity);

    setAddedToast(true);
    setTimeout(() => setAddedToast(false), 3000);
  };

  const handleWishlist = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/product/${slug}`)}`);
      return;
    }
    setWishlistSaving(true);
    try {
      await toggleWishlist({ ...product, images });
    } catch (_error) {
      // The shared wishlist context restores the previous state and exposes the error.
    } finally {
      setWishlistSaving(false);
    }
  };

  return (
    <div className="container py-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb small text-muted">
          <li className="breadcrumb-item">
            <Link to="/shop" className="text-reset text-decoration-none">
              Shop
            </Link>
          </li>
          {category_name && (
            <li className="breadcrumb-item">
              <Link to={`/shop?category=${category_slug}`} className="text-reset text-decoration-none">
                {category_name}
              </Link>
            </li>
          )}
          <li className="breadcrumb-item active text-primary fw-bold" aria-current="page">
            {title}
          </li>
        </ol>
      </nav>

      <div className="row g-5">
        {/* Left Column: Gallery */}
        <div className="col-12 col-md-6">
          <div className="border rounded-md overflow-hidden bg-card mb-3 shadow-sm" style={{ aspectRatio: '1/1' }}>
            <FramedImage
              src={images[selectedImage]}
              alt={title}
            />
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="d-flex gap-2 overflow-auto pb-2">
              {images.map((imgUrl, idx) => (
                <div
                  key={idx}
                  className={`border rounded p-1 cursor-pointer overflow-hidden ${
                    selectedImage === idx ? 'border-primary border-2' : ''
                  }`}
                  style={{ width: '70px', height: '70px', flexShrink: 0 }}
                  onClick={() => setSelectedImage(idx)}
                >
                  <FramedImage src={imgUrl} alt="" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Details */}
        <div className="col-12 col-md-6">
          {category_name && (
            <span className="text-primary font-weight-bold text-uppercase small" style={{ letterSpacing: '0.05em' }}>
              {category_name}
            </span>
          )}

          <div className="product-detail-heading">
            <h1 className="brand-font my-2">{title}</h1>
            <button
              type="button"
              className={`product-detail-wishlist${isWishlisted(product.id) ? ' is-saved' : ''}`}
              onClick={handleWishlist}
              disabled={wishlistSaving}
              aria-label={user
                ? `${isWishlisted(product.id) ? 'Remove' : 'Save'} ${title} ${isWishlisted(product.id) ? 'from' : 'to'} your wishlist`
                : `Sign in to save ${title} to your wishlist`}
            >
              <WishlistIcon filled={isWishlisted(product.id)} />
              <span>{isWishlisted(product.id) ? 'Saved' : 'Save'}</span>
            </button>
          </div>

          {subtitle && <p className="lead text-muted small mb-3">{subtitle}</p>}

            {/* Sequential 3-Level Pricing Breakdown */}
          <div className="p-3 border rounded bg-card mb-4">
            <div className="d-flex align-items-baseline gap-3 mb-2">
              <span className="fs-2 font-weight-bold text-primary">₹{displayedPrice.toFixed(2)}</span>
              {displayedMrp > displayedPrice && (
                <span className="text-muted text-decoration-line-through fs-5">₹{displayedMrp.toFixed(2)}</span>
              )}
              {displayedSavings > 0 && <span className="badge bg-success fs-6">Save ₹{displayedSavings.toFixed(2)}</span>}
            </div>

            {/* Discount Formula Explanation */}
            <div className="small text-muted border-top pt-2 mt-2">
              <span>Discounts applied: </span>
              {Number(product_discount_pct) > 0 && (
                <span className="badge bg-danger me-1">Product: {product_discount_pct}%</span>
              )}
              {Number(category_discount_pct) > 0 && (
                <span className="badge bg-warning text-dark me-1">Category: {category_discount_pct}%</span>
              )}
              {Number(site_discount_pct) > 0 && (
                <span className="badge bg-primary me-1">Site-wide: {site_discount_pct}%</span>
              )}
              {Number(product_discount_pct) === 0 && Number(category_discount_pct) === 0 && Number(site_discount_pct) === 0 && (
                <span>Standard MRP</span>
              )}
            </div>
          </div>

          {/* Variants Selector */}
          {variants.length > 0 && (
            <div className="mb-4">
              <label className="form-label font-weight-bold small">Select size{variants.some((variant) => variant.color) ? ' and style' : ''}:</label>
              <div className="d-flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    className={`btn btn-sm ${
                      selectedVariant?.id === v.id ? 'btn-primary-custom' : 'btn-outline-custom'
                    }`}
                    onClick={() => setSelectedVariant(v)}
                  >
                    {v.variant_name || [v.size, v.color, v.material].filter(Boolean).join(' · ') || v.sku}
                  </button>
                ))}
              </div>
              {selectedVariantLabel && <div className="form-text mt-2">Selected: {selectedVariantLabel}</div>}
            </div>
          )}

          {/* Quantity & Stock */}
          <div className="mb-4 d-flex align-items-center gap-4">
            <div>
              <label className="form-label font-weight-bold small d-block">Quantity:</label>
              <div className="input-group input-group-sm" style={{ width: '120px' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  -
                </button>
                <input
                  type="number"
                  className="form-control text-center"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                />
                <button className="btn btn-outline-secondary" onClick={() => setQuantity(quantity + 1)}>
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="form-label font-weight-bold small d-block">Availability:</label>
              <span className={`badge ${displayedStock > 0 ? 'bg-success' : 'bg-danger'} px-3 py-2`}>
                {displayedStock > 5 ? `In Stock (${displayedStock} available)` : displayedStock > 0 ? `Low Stock (${displayedStock} left)` : 'Out of Stock'}
              </span>
            </div>
          </div>

          {/* Add to Cart CTA */}
          <button
            onClick={handleAddToCart}
            disabled={displayedStock <= 0 || cartLoading}
            className="btn btn-lg btn-primary-custom w-100 rounded-pill py-3 font-weight-bold shadow-sm mb-3"
          >
            {cartLoading ? 'Preparing cart…' : `🛒 Add to Cart (₹${(displayedPrice * quantity).toFixed(2)})`}
          </button>

          {addedToast && (
            <div className="alert alert-success py-2 text-center small fade show">
              ✓ Item added to your shopping cart!
            </div>
          )}

          {/* Product Metadata & Specifications */}
          <div className="border-top pt-4 mt-4 text-muted small">
            {sku && <div className="mb-1"><strong>SKU:</strong> {sku}</div>}
            {weight && <div className="mb-1"><strong>Weight:</strong> {weight}g</div>}
            {dimensions && <div className="mb-1"><strong>Dimensions:</strong> {dimensions}</div>}
            {origin_label && <div className="mb-1"><strong>Made at:</strong> {origin_label}{origin_city ? ` · ${origin_city}` : ''}</div>}
            <div className="mb-1"><strong>Preparation time:</strong> {formatBusinessDays(preparation_days ?? DEFAULT_PREPARATION_DAYS)}</div>
            <div className="mb-1">Delivery is calculated from this source at checkout.</div>
            <div className="mt-3 p-3 border rounded bg-card">
              🌿 <strong>Handmade Guarantee:</strong> Every piece is individually crafted by hand. Minor organic variations make your piece uniquely yours.
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="mt-5 border-top pt-4">
        <h4 className="brand-font mb-3">Product Description</h4>
        <div className="p-4 border rounded bg-card leading-relaxed">
          {description || short_description || 'No detailed description available.'}
        </div>
      </div>
    </div>
  );
}
